import { useCallback, useMemo } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useAdminEventChatPermissions(context) {
  const {
    resolveAdminPermission,
    resolveEventDetailPermission,
    user,
  } = context;
    const adminRole = useMemo(
      () => String(user?.role || 'general').trim().toLowerCase() || 'general',
      [user?.role]
    );
    const adminPermissions = useMemo(() => {
      if (Array.isArray(user?.permissions)) return user.permissions;
      if (user?.permissions) return [user.permissions];
      return [];
    }, [user?.permissions]);
    const canUseEventChat = useMemo(
      () =>
        resolveAdminPermission({
          role: adminRole,
          isExternalAccount: user?.is_external_account === true,
          permissions: adminPermissions,
          permissionKey: 'NAV_EVENT_CHAT',
        }),
      [adminPermissions, adminRole, user?.is_external_account]
    );
    const canUseEventChatForSession = useCallback(
      (sessionUser) =>
        resolveAdminPermission({
          role: sessionUser?.role || 'general',
          isExternalAccount: sessionUser?.is_external_account === true,
          permissions: Array.isArray(sessionUser?.permissions)
            ? sessionUser.permissions
            : sessionUser?.permissions
              ? [sessionUser.permissions]
              : [],
          permissionKey: 'NAV_EVENT_CHAT',
        }),
      []
    );
    const canViewParticipants = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_VIEW_PARTICIPANTS',
        }),
      [adminPermissions, adminRole]
    );
    const canCreateDerivedGroupChat = useMemo(
      () =>
        canViewParticipants &&
        resolveAdminPermission({
          role: adminRole,
          isExternalAccount: user?.is_external_account === true,
          permissions: adminPermissions,
          permissionKey: 'NAV_GROUP_CHAT',
        }),
      [adminPermissions, adminRole, canViewParticipants, user?.is_external_account]
    );
    const canViewContractStatus = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_VIEW_CONTRACT_STATUS',
        }),
      [adminPermissions, adminRole]
    );
    const canViewCastingSettings = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_VIEW_CASTING_SETTINGS',
        }),
      [adminPermissions, adminRole]
    );
    const canViewCastingInfo = canViewCastingSettings || canViewParticipants || canViewContractStatus;
    const canViewCastingProgress = canViewParticipants || canViewContractStatus;
    const canViewTicketSales = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_VIEW_TICKET_SALES',
        }),
      [adminPermissions, adminRole]
    );
    const canViewEventTasks = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_VIEW_EVENT_TASKS',
        }),
      [adminPermissions, adminRole]
    );
    const canViewEventNotes = useMemo(
      () =>
        resolveAdminPermission({
          role: adminRole,
          isExternalAccount: user?.is_external_account === true,
          permissions: adminPermissions,
          permissionKey: 'EVENT_CHAT_VIEW_NOTES',
        }),
      [adminPermissions, adminRole, user?.is_external_account]
    );
    const canManageEventNotes = useMemo(
      () =>
        resolveAdminPermission({
          role: adminRole,
          isExternalAccount: user?.is_external_account === true,
          permissions: adminPermissions,
          permissionKey: 'EVENT_CHAT_EDIT_NOTES',
        }),
      [adminPermissions, adminRole, user?.is_external_account]
    );
    const canViewMaterials = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_VIEW_MATERIALS',
        }),
      [adminPermissions, adminRole]
    );
    const canEditMaterials = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_EDIT_INFO',
        }) &&
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_EDIT_MATERIALS',
        }),
      [adminPermissions, adminRole]
    );
    const canDownloadMaterials = useMemo(
      () =>
        canViewMaterials &&
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_DOWNLOAD_MATERIALS',
        }),
      [adminPermissions, adminRole, canViewMaterials]
    );
    const canViewMaterialDownloadLogs = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_VIEW_MATERIAL_DOWNLOAD_LOGS',
        }),
      [adminPermissions, adminRole]
    );
    const canViewAlbum = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_VIEW_IMAGES',
        }),
      [adminPermissions, adminRole]
    );
    const canEditAlbum = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_EDIT_IMAGES',
        }),
      [adminPermissions, adminRole]
    );
    const canDownloadAlbum = useMemo(
      () =>
        resolveEventDetailPermission({
          role: adminRole,
          permissions: adminPermissions,
          permissionKey: 'EVENT_DOWNLOAD_IMAGES',
        }),
      [adminPermissions, adminRole]
    );
  return {
    canUseEventChat,
    canUseEventChatForSession,
    canViewCastingInfo,
    canViewCastingProgress,
    canCreateDerivedGroupChat,
    canViewTicketSales,
    canViewEventTasks,
    canViewEventNotes,
    canManageEventNotes,
    canViewMaterials,
    canEditMaterials,
    canDownloadMaterials,
    canViewMaterialDownloadLogs,
    canViewAlbum,
    canEditAlbum,
    canDownloadAlbum,
  };
}
